import { type AddToCart } from '@redteclab/api/clients/bully'

import { apmCaptureError } from '../../apm'
import { CustomError } from '../../base-errors'
import { useSearchParamDependentOpenableControls } from '../../generic-hooks'
import { userApiSwrRefetchMiniCart } from '../../user-data'
import {
  type CartIntermediateAddToCartProps,
  useCartIntermediateApiSwrProductAddToCartMutation,
} from '../api/swr/useCartIntermediateApiSwrProductAddToCartMutation'

export const useProductAddToCart = ({
  currentPrescriptionType,
  offerId,
  quantity,
}: CartIntermediateAddToCartProps): {
  addToCartResponseData: AddToCart | null | undefined
  handleAddToCart: () => Promise<{
    data: AddToCart | null
    success: boolean
  }>
  isAddProductToCartSuccessful: boolean
  isMutating: boolean
} => {
  const { handleOpen: openCartIntemediateSlider } =
    useSearchParamDependentOpenableControls({
      paramName: 'cartIntermediateDrawer',
    })

  const productAddToCartMutation =
    useCartIntermediateApiSwrProductAddToCartMutation({
      currentPrescriptionType,
      offerId,
      quantity,
    })

  const handleAddToCart = async (): Promise<{
    data: AddToCart | null
    success: boolean
  }> => {
    try {
      await productAddToCartMutation.trigger()
      await userApiSwrRefetchMiniCart()

      return { data: productAddToCartMutation.data ?? null, success: true }
    } catch (error) {
      apmCaptureError(
        new CustomError(
          {
            message: 'failed to add product to the cart',
            name: 'CART_INTERMEDIATE_ADD_TO_CART_FAIL',
          },
          {
            cause: error,
          },
        ),
      )

      return { data: null, success: false }
    } finally {
      openCartIntemediateSlider()
    }
  }

  return {
    addToCartResponseData: productAddToCartMutation.data,
    handleAddToCart,
    isAddProductToCartSuccessful: Boolean(
      productAddToCartMutation.data && !productAddToCartMutation.error,
    ),
    isMutating: productAddToCartMutation.isMutating,
  }
}
